/**
 * Form Components
 *
 * Reusable form input components for accounting forms.
 */

import React from 'react';

// =============================================================================
// STYLES
// =============================================================================

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 0.75rem',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 500,
  marginBottom: '0.375rem',
  color: 'var(--text, #111827)',
};

const errorStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#ef4444',
  marginTop: '0.25rem',
};

// =============================================================================
// FORM FIELD
// =============================================================================

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

export function FormField({ label, required, error, children }: FormFieldProps) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
      </label>
      {children}
      {error && <div style={errorStyle}>{error}</div>}
    </div>
  );
}

// =============================================================================
// TEXT INPUT
// =============================================================================

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
  disabled?: boolean;
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled,
}: TextInputProps) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        ...inputStyle,
        background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
      }}
    />
  );
}

// =============================================================================
// NUMBER INPUT
// =============================================================================

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

export function NumberInput({
  value,
  onChange,
  placeholder,
  min,
  max,
  step = 1,
  disabled,
}: NumberInputProps) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      style={{
        ...inputStyle,
        background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
      }}
    />
  );
}

// =============================================================================
// CURRENCY INPUT
// =============================================================================

interface CurrencyInputProps {
  value: number; // in cents
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function CurrencyInput({
  value,
  onChange,
  placeholder = '0,00',
  disabled,
}: CurrencyInputProps) {
  const displayValue = (value / 100).toFixed(2).replace('.', ',');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onChange(Math.round(parsed * 100));
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <span
        style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted, #6b7280)',
          fontSize: '0.875rem',
        }}
      >
        €
      </span>
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          ...inputStyle,
          paddingLeft: '1.75rem',
          background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
        }}
      />
    </div>
  );
}

// =============================================================================
// TEXTAREA
// =============================================================================

interface TextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  disabled,
}: TextAreaProps) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      style={{
        ...inputStyle,
        resize: 'vertical',
        background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
      }}
    />
  );
}

// =============================================================================
// SELECT
// =============================================================================

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: SelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        ...inputStyle,
        background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// =============================================================================
// DATE INPUT
// =============================================================================

interface DateInputProps {
  value: string; // ISO date string
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function DateInput({ value, onChange, disabled }: DateInputProps) {
  const dateValue = value ? value.split('T')[0] : '';

  return (
    <input
      type="date"
      value={dateValue}
      onChange={(e) => onChange(e.target.value ? `${e.target.value}T00:00:00Z` : '')}
      disabled={disabled}
      style={{
        ...inputStyle,
        background: disabled ? 'var(--bg-secondary, #f9fafb)' : 'var(--bg, white)',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
      }}
    />
  );
}

// =============================================================================
// FORM ROW (for side-by-side fields)
// =============================================================================

interface FormRowProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
}

export function FormRow({ children, columns = 2 }: FormRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '1rem',
      }}
    >
      {children}
    </div>
  );
}

// =============================================================================
// FORM SECTION
// =============================================================================

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
}

export function FormSection({ title, children }: FormSectionProps) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3
        style={{
          fontSize: '0.9375rem',
          fontWeight: 600,
          marginBottom: '1rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid var(--border, #e5e7eb)',
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

// =============================================================================
// FORM ACTIONS
// =============================================================================

interface FormActionsProps {
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
}

export function FormActions({
  onCancel,
  onSubmit,
  submitLabel = 'Salva',
  cancelLabel = 'Annulla',
  isSubmitting,
}: FormActionsProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid var(--border, #e5e7eb)',
      }}
    >
      <button
        type="button"
        onClick={onCancel}
        disabled={isSubmitting}
        style={{
          padding: '0.625rem 1.25rem',
          background: 'var(--bg-secondary, #f9fafb)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
        }}
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={isSubmitting}
        style={{
          padding: '0.625rem 1.25rem',
          background: 'var(--brand, #3b82f6)',
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          opacity: isSubmitting ? 0.7 : 1,
        }}
      >
        {isSubmitting ? 'Salvataggio...' : submitLabel}
      </button>
    </div>
  );
}
